import type { PostHogContextRef } from '../context'

/**
 * Share method — copy-to-clipboard or browser native share.
 */
export type CommunityShareMethod = 'copy_link' | 'native_share'

/**
 * Followers / Following modal tab identifier.
 */
export type CommunityConnectionsTab = 'followers' | 'following'

/**
 * Profile visibility at create-time — `community_profile_created.visibility`.
 */
export type CommunityProfileVisibility = 'public' | 'private'

/**
 * Community profile action events. Fire from profile view, settings, and share
 * UI surfaces.
 *
 * `community_member_profile_followed` / `_unfollowed` / `community_profile_shared`
 * carry the *target* profile's display name — NOT a UUID. Display names are
 * user-chosen public identifiers, much less sensitive than the underlying
 * account UUID. The logged-in user (the *actor* of the follow/unfollow/share)
 * is identified automatically via PostHog `identify()` person properties.
 *
 * `community_profile_created` is the *activation* event — fires only on the
 * first successful profile save (i.e. when the user had no profile at mount).
 * Subsequent saves fire `community_profile_edited` instead.
 *
 * Member-scoped events (`community_member_*`) fire ONLY when a logged-in user
 * is acting on ANOTHER member's profile / connections — never on their own.
 * They are the clean signal for "community engagement with other members"
 * dashboards, replacing the fragile `is_own_profile=false` filter on the
 * generic events. Two surface-distinct events for follow/unfollow:
 * `community_member_profile_*` fires from the profile-gradient-header button;
 * `community_member_connection_modal_*` fires from inside the ConnectionsModal.
 */
export type ProfileEvents = {
  community_member_profile_followed: (targetDisplayName?: string) => void
  community_member_profile_unfollowed: (targetDisplayName?: string) => void
  community_member_profile_viewed: (
    isLoggedIn: boolean,
    targetDisplayName?: string,
    viewerIsFollowing?: boolean,
  ) => void
  community_member_connections_viewed: (
    tab: CommunityConnectionsTab,
    targetDisplayName?: string,
  ) => void
  community_member_connection_modal_followed: (targetDisplayName?: string) => void
  community_member_connection_modal_unfollowed: (targetDisplayName?: string) => void
  community_profile_shared: (method: CommunityShareMethod, targetDisplayName?: string) => void
  community_profile_edited: () => void
  community_profile_created: (
    hasBio: boolean,
    hasAvatar: boolean,
    visibility: CommunityProfileVisibility,
    archetype?: string,
    profileCompleteness?: number,
  ) => void
  community_connections_viewed: (tab: CommunityConnectionsTab) => void
}

export function createProfileEventCallbacks (
  contextRef: PostHogContextRef,
): ProfileEvents {
  return {
    community_member_profile_followed: (targetDisplayName?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_profile_followed', {
        context: 'community',
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_member_profile_unfollowed: (targetDisplayName?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_profile_unfollowed', {
        context: 'community',
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_member_profile_viewed: (
      isLoggedIn: boolean,
      targetDisplayName?: string,
      viewerIsFollowing?: boolean,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_profile_viewed', {
        context: 'community',
        is_logged_in: isLoggedIn,
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
        ...(viewerIsFollowing != null && { viewer_is_following: viewerIsFollowing }),
      })
    },

    community_member_connections_viewed: (
      tab: CommunityConnectionsTab,
      targetDisplayName?: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_connections_viewed', {
        context: 'community',
        tab,
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_member_connection_modal_followed: (targetDisplayName?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_connection_modal_followed', {
        context: 'community',
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_member_connection_modal_unfollowed: (targetDisplayName?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_member_connection_modal_unfollowed', {
        context: 'community',
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_profile_shared: (method: CommunityShareMethod, targetDisplayName?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_profile_shared', {
        context: 'community',
        method,
        ...(targetDisplayName && { target_display_name: targetDisplayName }),
      })
    },

    community_profile_edited: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_profile_edited', {
        context: 'community',
      })
    },

    community_profile_created: (
      hasBio: boolean,
      hasAvatar: boolean,
      visibility: CommunityProfileVisibility,
      archetype?: string,
      profileCompleteness?: number,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_profile_created', {
        context: 'community',
        has_bio: hasBio,
        has_avatar: hasAvatar,
        visibility,
        ...(archetype && { archetype }),
        ...(profileCompleteness != null && { profile_completeness: profileCompleteness }),
      })
    },

    community_connections_viewed: (tab: CommunityConnectionsTab) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_connections_viewed', {
        context: 'community',
        tab,
      })
    },
  }
}
